import React, { useState } from 'react'
import cn from 'classnames'

import { Button, Icon } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { EventTypes, track } from '@mc/track-event'

import { ModuleTaskList } from '../ModuleTaskList'
import type { Module } from '../../types'
import { useSessionsLandingPageData } from '../SessionsLandingPageDataProvider'
import { CampModuleNumber } from '../CampModuleNumber'
import style from './CampModule.module.scss'

interface CampModuleProps {
  module: Module
  className?: string
}

export const CampModule = ({
  module: { id: moduleId, description, name, number, tasks },
  className,
}: CampModuleProps) => {
  const { t } = useTranslation('@mc/next')
  const { camp } = useSessionsLandingPageData()
  const { slug: campSlug } = camp

  const isInitiallyOpen = number === 1
  const [isOpen, setIsOpen] = useState(isInitiallyOpen)

  const onClick = () => {
    setIsOpen(!isOpen)

    track(EventTypes.COURSE_MARKETING_CLICK, {
      category: 'curriculum',
      location: 'body',
      action: name,
      page_type: 'class-page',
      class_type: 'camp',
      class: campSlug,
      instructor_coming_soon: false,
    })
  }

  return (
    <div role='group' aria-label='module'>
      <div className={cn(style.campModule, className)}>
        {/* The arrow button controls the area for keyboard and screen reader users
         but this area is clickable for mouse users */}
        {/* eslint-disable-next-line */}
        <div className='container--md mc-clickable' onClick={onClick}>
          <div className='row no-gutters-vertical'>
            <div className='col-auto mc-py-4 align-self-stretch'>
              <div
                className={cn(style.progressBar, {
                  [style.progressBarOpen]: isOpen,
                })}
              >
                <div className={cn('mc-py-4', style.progressBarNode)}>
                  <CampModuleNumber moduleNumber={number} />
                </div>
              </div>
            </div>
            <div className='col d-flex flex-column justify-content-center'>
              <div className='mc-py-4'>
                <h5 className='mc-text-h5'>{name}</h5>
                {isOpen && (
                  <div className='mc-mt-4'>
                    <p>{description}</p>
                    <p className='mc-text-small mc-mt-2'></p>
                  </div>
                )}
              </div>
            </div>
            <div className='col-auto d-flex'>
              <Button
                onClick={onClick}
                kind='link'
                symmetrical
                aria-label={t(
                  isOpen
                    ? 'sessions.curriculum.hideTasks'
                    : 'sessions.curriculum.showTasks',
                  { number },
                )}
                aria-controls={`module-${moduleId}`}
                aria-expanded={isOpen}
              >
                <Icon kind={isOpen ? 'chevron-up' : 'chevron-down'} />
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isOpen && (
        <div id={`module-${moduleId}`} className='mc-bg-gray-700 mc-py-6'>
          <div className='container--md'>
            <ModuleTaskList tasks={tasks} />
          </div>
        </div>
      )}
    </div>
  )
}
